/**
 * CTADialog — janela de compra (porta de `showCTA` [application.js:2325] + showAuthorize), no
 * visual da proposta "Elements" (17/10/2026):
 *  - teste da Máquina de Leads, licença vencida e eMiner: texto, números da conta (contatos e
 *    listas, quando há), vantagens, "Ver planos disponíveis" (checkout com o link promo) e
 *    "Já comprei? Liberar o acesso";
 *  - LeadExplorer: só o texto e "Contratar";
 *  - "Liberar o acesso agora": email + código → POST /app/purchase; sucesso atualiza a
 *    expiração (sessão + sync) e mostra "Acesso liberado" com a nova data.
 *
 * Movimento (23/10/2026): a janela entra e sai com .ds-veu/.ds-dialogo (fica montada enquanto
 * sai); "Ver planos disponíveis" com o .ds-brilho, laço que só vive com a janela aberta (M3 a);
 * "Acesso liberado" com o ✓ (.ds-sucesso), o confete (compra é marco da regra 9, M7 b) e a data
 * nova da licença rolando no lugar (.ds-rola).
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBrand } from '../../hooks/useBrand';
import { useSessionStore } from '../../stores/sessionStore';
import { useCtaStore, type CtaTool } from './ctaStore';
import { postPurchase } from '../../api/useOnboarding';
import { useLists } from '../../api/useLists';
import { genPromo } from '../../lib/promo';
import { getCookie } from '../../lib/cookies';
import { setSync } from '../../lib/extensionBridge';
import { externalUrls } from '../../config/env';
import { emitActivity } from '../../lib/activity';
import { showConfetti } from '../../lib/celebrate';
import { semMovimento } from '../../lib/movimento';
import { isTrial } from '../../lib/entitlements';
import { usePresenca } from '../../hooks/usePresenca';
import { Html, ScreenDialog, SuccessBurst } from '../../components/screen/Screen';
import { ActivationForm, type FormError } from '../../components/screen/ActivationForm';
import { useScreenText, type ScreenKey } from '../../components/screen/screenText';

/** IDs de preço dos módulos avulsos (urls.price_* [application.js:56-57]). */
const PRICE_LEADEXPLORER = '69692eec4bae0c60961efb74';

function checkoutUrl(tool: CtaTool, isLeadMachine: boolean, email: string): string {
  if (tool === 'lead-explorer') {
    let url = externalUrls.buyLeadMachine;
    if (email) url += '/i/' + genPromo({ prices: PRICE_LEADEXPLORER, email });
    return url;
  }
  if (isLeadMachine) {
    let url = externalUrls.buyLeadMachine;
    if (email) url += '/' + genPromo({ email });
    return url;
  }
  return externalUrls.buyEminer;
}

type Variant = 'trial' | 'expired' | 'em' | 'le';
type Step = 'offer' | 'unlock' | 'done';

export function CTADialog(): JSX.Element | null {
  const tool = useCtaStore((s) => s.tool);
  // Montado só com a janela aberta (e enquanto ela sai): as listas (para os números) não são
  // pedidas à toa. Cada abertura depois que a janela saiu de vez é uma janela nova, com o estado
  // do zero. Reaberta durante a saída (140 ms), é a mesma janela que volta do meio (catálogo,
  // "Janela e diálogo"); para outra oferta (outro tool), janela nova.
  const { montado } = usePresenca(Boolean(tool));
  const [antes, setAntes] = useState<CtaTool | null>(tool);
  const [ultimo, setUltimo] = useState<CtaTool | null>(tool);
  const [vez, setVez] = useState(0);
  const [vivo, setVivo] = useState(montado);
  if (montado !== vivo) {
    setVivo(montado);
    // Saiu de vez: a próxima abertura monta uma janela nova.
    if (!montado) setVez((n) => n + 1);
  }
  if (tool !== antes) {
    setAntes(tool);
    if (tool) {
      setUltimo(tool);
      if (!antes && vivo && tool !== ultimo) setVez((n) => n + 1);
    }
  }
  return montado && ultimo ? <CtaWindow key={vez} tool={ultimo} open={Boolean(tool)} /> : null;
}

/** Data da licença antes e depois da compra: a nova rola no lugar da antiga. */
type Until = { before: string | null; after: string };

function CtaWindow({ tool, open }: { tool: CtaTool; open: boolean }): JSX.Element {
  const { tx } = useScreenText();
  const brand = useBrand();
  const qc = useQueryClient();
  const session = useSessionStore((s) => s.session);
  const closeCTA = useCtaStore((s) => s.closeCTA);
  // Aberta por "Tenho um código de ativação": começa no campo do código.
  const [step, setStep] = useState<Step>(() =>
    useCtaStore.getState().start === 'unlock' ? 'unlock' : 'offer',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FormError | null>(null);
  const [until, setUntil] = useState<Until | null>(null);

  const isLeadMachine = brand.source === 'lead-machine';
  const variant: Variant =
    tool === 'lead-explorer'
      ? 'le'
      : !isLeadMachine
        ? 'em'
        : isTrial(session)
          ? 'trial'
          : 'expired';

  useEffect(() => {
    if (!open) return;
    emitActivity('paywall.shown', { tool, trigger: variant === 'le' ? 'feature' : 'expired' });
    // Uma vez por abertura do paywall (também quando a janela volta do meio da saída).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function subscribe(): void {
    emitActivity('paywall.clicked', { tool });
    window.open(checkoutUrl(tool, isLeadMachine, session?.email ?? ''), '_blank');
    closeCTA();
  }

  function unlock(email: string, code: string): void {
    const before = session?.expiredAt ? String(session.expiredAt) : null;
    setBusy(true);
    setError(null);
    const userId = getCookie('userId') ?? session?.userId ?? '';
    void postPurchase(email, code, userId)
      .then((res) => {
        setBusy(false);
        if (!res.error) {
          emitActivity('purchase.code_redeemed');
          if (res.expiredAt) {
            setSync({ expire: res.expiredAt });
            void qc.invalidateQueries({ queryKey: ['me'] });
            // legado showCTA.signUp → showParticles [2344]. O canvas-confetti já respeita o
            // "reduzir movimento" do sistema; a opção do produto, aqui.
            if (!semMovimento()) showConfetti();
            setUntil({ before, after: String(res.expiredAt) });
            setStep('done');
          } else {
            closeCTA();
          }
          return;
        }
        if (res.error === 'user_inactive') setError({ field: 'form', text: tx('err_inactive') });
        else if (res.error === 'invalid_code') setError({ field: 'code', text: tx('err_invalid') });
        else if (res.error === 'expired_code') setError({ field: 'code', text: tx('act_expired') });
        else setError({ field: 'form', text: tx('err_generic') });
      })
      .catch(() => {
        setBusy(false);
        setError({ field: 'form', text: tx('err_generic') });
      });
  }

  if (step === 'done') {
    return (
      <ScreenDialog open={open} closeLabel={tx('close')} onClose={closeCTA}>
        <div className="as-released">
          <SuccessBurst title={tx('released')} />
          {until ? (
            <span className="as-chip">
              <span aria-hidden="true" />
              <ActiveUntil until={until} />
            </span>
          ) : null}
          <button
            type="button"
            className="as-btn as-btn--dark as-btn--block ds-resposta"
            onClick={closeCTA}
          >
            {tx('continue')}
          </button>
        </div>
      </ScreenDialog>
    );
  }

  if (step === 'unlock') {
    return (
      <ScreenDialog
        open={open}
        title={tx('unlock_title')}
        closeLabel={tx('close')}
        backLabel={tx('back')}
        onBack={() => {
          setError(null);
          setStep('offer');
        }}
        onClose={closeCTA}
      >
        <ActivationForm
          submitLabel={tx('act_submit')}
          busyLabel={tx('act_submitting')}
          busy={busy}
          error={error}
          onSubmit={unlock}
          onEdit={() => setError(null)}
        />
      </ScreenDialog>
    );
  }

  return (
    <ScreenDialog
      open={open}
      icon="workspace_premium"
      title={tx(`cta_${variant}_title`)}
      closeLabel={tx('close')}
      onClose={closeCTA}
    >
      <Html className="as-dialog__text" html={tx(`cta_${variant}_text`)} />
      {variant === 'le' ? (
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--block ds-resposta"
          onClick={subscribe}
        >
          {tx('contract')}
        </button>
      ) : (
        <>
          <CtaStats variant={variant} />
          <ul className="as-benefits">
            {(['b_unlimited', 'b_download', variant === 'em' ? 'b_all' : 'b_tools'] as const).map(
              (key) => (
                <li key={key}>
                  <span className="material-symbols-rounded" aria-hidden="true">
                    check
                  </span>
                  {tx(key)}
                </li>
              ),
            )}
          </ul>
          <button
            type="button"
            className="as-btn as-btn--primary as-btn--block ds-resposta ds-brilho"
            onClick={subscribe}
          >
            {tx('see_plans')}
            <span className="material-symbols-rounded" aria-hidden="true">
              arrow_forward
            </span>
          </button>
          <button
            type="button"
            className="as-link as-link--muted as-link--center ds-resposta"
            onClick={() => setStep('unlock')}
          >
            {tx('already')}
          </button>
        </>
      )}
    </ScreenDialog>
  );
}

/** Marca para achar a data dentro do texto traduzido ("Licença ativa até {{date}}"). */
const DATE_MARK = '§date§';

/**
 * "Licença ativa até DATA" com a data nova rolando no lugar da antiga (.ds-rola, 200 ms, depois do
 * ✓). O leitor de tela ouve só a data nova. Sem data antiga (ou igual), a nova fica parada.
 */
function ActiveUntil({ until }: { until: Until }): JSX.Element {
  const { tx, date } = useScreenText();
  const after = date(until.after);
  const valid = until.before != null && !Number.isNaN(new Date(until.before).getTime());
  const before = valid && until.before ? date(until.before) : null;
  const parts = tx('active_until', { date: DATE_MARK }).split(DATE_MARK);
  if (parts.length !== 2 || !before || before === after) {
    return <span>{tx('active_until', { date: after })}</span>;
  }
  return (
    <span>
      {parts[0]}
      <span className="ds-rola">
        <span className="ds-rola-sai" aria-hidden="true">
          {before}
        </span>
        <span className="ds-rola-entra">{after}</span>
      </span>
      {parts[1]}
    </span>
  );
}

/** Números da conta: some se ainda não há listas (zero não convence ninguém). */
function CtaStats({ variant }: { variant: Variant }): JSX.Element | null {
  const { tx, number } = useScreenText();
  const places = useLists('places');
  const phones = useLists('phones');
  const emails = useLists('emails');
  const all = [...(places.data ?? []), ...(phones.data ?? []), ...(emails.data ?? [])].filter(
    (l) => Number(l.count) > 0,
  );
  const businesses = (places.data ?? []).reduce((sum, l) => sum + (Number(l.count) || 0), 0);
  const contacts = all.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
  if (!all.length) return null;

  const first: [number, ScreenKey] =
    variant === 'expired'
      ? [businesses || contacts, businesses ? 'stat_business' : 'stat_contacts']
      : [contacts, variant === 'trial' ? 'stat_contacts_trial' : 'stat_contacts'];
  const second: ScreenKey = variant === 'expired' ? 'stat_lists_saved' : 'stat_lists';
  return (
    <div className="as-stats">
      <div>
        <strong>{number(first[0])}</strong>
        <span>{tx(first[1])}</span>
      </div>
      <div>
        <strong>{number(all.length)}</strong>
        <span>{tx(second)}</span>
      </div>
    </div>
  );
}
